import { useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import classnames from "classnames";

import { usePermissions } from "common/core/current_user_role";
import Button from "common/core/button";
import IconButton from "common/core/button/icon_button";
import LoadingIndicator from "common/core/loading_indicator";
import { Heading } from "common/core/typography";
import modalScrollContent from "common/core/modal_scroll_content";
import { userFullName } from "util/user";
import IdentityDocumentViewer, {
  parsePhotosForDocumentViewer,
} from "common/identity/document_viewer";
import IdentityTimeline from "common/identity/timeline";
import { useMutation, useQuery } from "util/graphql";
import { Badge } from "common/core/badge";
import { FraudStatusEnum, FraudStatusInputEnum } from "graphql_globals";

import { ConfirmationModal } from "./fraud";
import Styles from "./index.module.scss";
import IdentityDetailsQuery, {
  type IdentityDetails_documentBundle_DocumentBundle as DocumentBundle,
  type IdentityDetails_documentBundle_DocumentBundle_organizationTransaction_customerSigners as TransactionCustomer,
  type IdentityDetails_documentBundle_DocumentBundle_organizationTransaction_customerSigners_signerIdentities as TransactionCustomerIdentity,
} from "./index.query.graphql";
import IdentityProfile from "./identity_profile";
import UpdateOrganizationTransactionCustomerFraudStatus from "./update_fraud_status.mutation.graphql";

type InnerProps = {
  documentBundle: DocumentBundle;
};

type OuterProps = {
  bundle: {
    id: string;
  };
};

const Messages = defineMessages({
  expand: {
    id: "305038c8-df4e-4255-9a52-b74fadf6e341",
    defaultMessage: "Expand content",
  },
  collapse: {
    id: "49a6dc64-ba50-46aa-a168-ebccb7fd1e24",
    defaultMessage: "Collapse content",
  },
});

function EmptyState(props: { className?: string }) {
  return (
    <div className={props.className || Styles.emptyState}>
      <FormattedMessage
        id="2b6dcb44-c2e6-43af-92ce-9e51ecbb7d1d"
        defaultMessage="Check back later..."
        tagName="h2"
      />
      <FormattedMessage
        id="242482a0-e746-4fe9-920d-d9dfdb710b15"
        defaultMessage="Identity information appears here after completion of the verification process."
        tagName="h3"
      />
    </div>
  );
}

function Signer(props: {
  customer: TransactionCustomer;
  signerIdentities: TransactionCustomerIdentity[];
  markAsFraud?: () => void;
  updatingFraudStatus: boolean;
  collapsible: boolean;
  index: number;
}) {
  const { customer, signerIdentities, markAsFraud, updatingFraudStatus, collapsible, index } =
    props;
  const intl = useIntl();
  const [expanded, setExpanded] = useState(true);
  const [showOlderResults, setShowOlderResults] = useState(false);
  const rest = signerIdentities.slice(1);
  const signerMarkedAsFraud = customer.fraudStatus?.status === FraudStatusEnum.FRAUD;

  return (
    <div>
      <div
        className={classnames(Styles.signerHeader, {
          [Styles.signerHeaderCollapsible]: collapsible,
        })}
        onClick={() => {
          if (collapsible) {
            setExpanded(!expanded);
          }
        }}
      >
        <div>
          {collapsible && (
            <IconButton
              iconClassName={Styles.dropdownIcon}
              buttonColor="dark"
              variant="tertiary"
              buttonSize="condensed"
              onClick={() => {
                setExpanded(!expanded);
              }}
              aria-expanded={expanded}
              aria-label={intl.formatMessage(expanded ? Messages.collapse : Messages.expand)}
              name={expanded ? "caret-up" : "caret-down"}
            />
          )}
          <Heading textStyle="headingOne" level="h2">
            {userFullName(customer)}
          </Heading>
          <FormattedMessage
            id="eb8f554d-651c-4da3-9325-4113a026a87b"
            defaultMessage="(Identity {signerIndex})"
            values={{ signerIndex: index + 1 }}
            tagName="span"
          />
          {signerMarkedAsFraud && (
            <Badge kind="warning">
              <FormattedMessage
                id="5dbae2c5-2798-41bb-a780-68723d23c8b0"
                defaultMessage="Identity flagged"
              />
            </Badge>
          )}
        </div>
        {Boolean(markAsFraud) && (
          <Button
            buttonColor="action"
            variant="secondary"
            onClick={markAsFraud}
            withIcon={{ name: "state", placement: "left" }}
            isLoading={updatingFraudStatus}
          >
            {signerMarkedAsFraud ? (
              <FormattedMessage
                id="a3b6d5e7-cb95-47e2-bcee-cfe0d0934c93"
                defaultMessage="Unflag identity"
              />
            ) : (
              <FormattedMessage
                id="c197cf38-c2d2-47e0-965f-7f6006eb7dc1"
                defaultMessage="Flag identity"
              />
            )}
          </Button>
        )}
      </div>
      {expanded && (
        <div>
          {signerIdentities[0] ? (
            <>
              <SignerIdentity
                signerIdentity={signerIdentities[0]}
                attempt={signerIdentities.length}
              />
              {showOlderResults &&
                rest.map((si, i) => (
                  <SignerIdentity
                    key={`si-${i}`}
                    signerIdentity={si}
                    attempt={signerIdentities.length - (i + 1)}
                  />
                ))}
              {Boolean(rest.length) && (
                <Button
                  variant="tertiary"
                  buttonColor="action"
                  onClick={() => {
                    setShowOlderResults(!showOlderResults);
                  }}
                >
                  {showOlderResults ? (
                    <FormattedMessage
                      id="c8ff3e0f-974d-4163-941d-a02a9ca1e1af"
                      defaultMessage="Hide previous attempts"
                    />
                  ) : (
                    <FormattedMessage
                      id="6a323f3b-52b1-466a-a485-4a7cde9598a0"
                      defaultMessage="Show previous attempts"
                    />
                  )}
                </Button>
              )}
            </>
          ) : (
            <EmptyState className={Styles.emptySignerIdentityState} />
          )}
        </div>
      )}
    </div>
  );
}

function SignerIdentity({
  attempt,
  signerIdentity,
}: {
  attempt: number;
  signerIdentity: TransactionCustomerIdentity;
}) {
  return (
    <div className={Styles.signerIdentity}>
      <Heading textStyle="headingSix" level="h2">
        <FormattedMessage
          id="fd8bdba1-650f-4be7-9c5f-53a2b5ebd08f"
          defaultMessage="Attempt {attempt}"
          values={{ attempt }}
        />
      </Heading>
      <IdentityProfile signerIdentity={signerIdentity} />
      <div>
        <IdentityTimeline
          identityTimeline={signerIdentity.identityTimeline}
          options={{ horizontal: true }}
        />
      </div>
      {signerIdentity.photoId && (
        <div>
          <IdentityDocumentViewer
            photos={parsePhotosForDocumentViewer(signerIdentity)}
            className={Styles.documentViewer}
          />
        </div>
      )}
    </div>
  );
}

type FraudActionState =
  | {
      loading: true;
      action: null;
      selectedCustomerId: null;
    }
  | {
      loading: false;
      action: FraudStatusInputEnum;
      selectedCustomerId: string;
    }
  | null;

function IdentityDetails(props: InnerProps) {
  const { documentBundle } = props;

  const { hasPermissionFor } = usePermissions();
  const [fraudActionState, setFraudActionState] = useState<FraudActionState>(null);
  const updateCustomerFraudStatus = useMutation(UpdateOrganizationTransactionCustomerFraudStatus);

  const customerSigners = documentBundle.organizationTransaction!.customerSigners;
  const groupedAndSortedSignerIdentities = customerSigners.map((signer) => ({
    customer: signer,
    signerIdentities: signer.signerIdentities
      ?.slice()
      .sort((a, b) => b.expiresAt.localeCompare(a.expiresAt)),
  }));
  const signerIdentitiesTotal = customerSigners.reduce((a, b) => {
    return a + (b.signerIdentities?.length ?? 0);
  }, 0);

  const handleConfirmUpdateFraudStatus = async ({
    reason: fraudStatusReason,
  }: {
    reason: string;
  }) => {
    if (!fraudActionState?.selectedCustomerId) {
      return null;
    }

    setFraudActionState({
      loading: true,
      selectedCustomerId: null,
      action: null,
    });

    await updateCustomerFraudStatus({
      variables: {
        input: {
          fraudStatus: fraudActionState.action,
          fraudStatusReason,
          transactionCustomerId: fraudActionState.selectedCustomerId,
        },
      },
    });

    setFraudActionState(null);
  };

  return (
    <>
      {fraudActionState?.selectedCustomerId && (
        <ConfirmationModal
          onClose={() => {
            setFraudActionState(null);
          }}
          onContinue={handleConfirmUpdateFraudStatus}
          action={fraudActionState.action}
        />
      )}
      <div className={Styles.mainContainer}>
        <div>
          <div className={Styles.betaBadge}>
            <FormattedMessage id="ad53c9ec-464b-4948-811d-c6e5f180041f" defaultMessage="Beta" />
          </div>
        </div>
        {signerIdentitiesTotal === 0 ? (
          <EmptyState />
        ) : (
          groupedAndSortedSignerIdentities.map(({ signerIdentities, customer }, i) => {
            if (!signerIdentities) {
              return null;
            }
            return (
              <Signer
                key={customer.id}
                customer={customer}
                signerIdentities={signerIdentities}
                collapsible={customerSigners.length > 1}
                markAsFraud={
                  hasPermissionFor("markCustomerAsFraudulent")
                    ? () => {
                        setFraudActionState({
                          loading: false,
                          selectedCustomerId: customer.id,
                          action:
                            customer.fraudStatus?.status === FraudStatusEnum.FRAUD
                              ? FraudStatusInputEnum.REMOVE_FRAUD
                              : FraudStatusInputEnum.FRAUD,
                        });
                      }
                    : undefined
                }
                updatingFraudStatus={fraudActionState?.loading ?? false}
                index={i}
              />
            );
          })
        )}
      </div>
    </>
  );
}

function IdentityDetailsWrapper({ bundle }: OuterProps) {
  const { data, loading } = useQuery(IdentityDetailsQuery, {
    variables: { documentBundleId: bundle.id },
  });

  if (loading || !data) {
    return <LoadingIndicator />;
  }

  if (data.documentBundle?.__typename !== "DocumentBundle") {
    throw new Error(`Expected DocumentBundle, got ${data.documentBundle?.__typename}`);
  }

  return <IdentityDetails documentBundle={data.documentBundle} />;
}

export default modalScrollContent(IdentityDetailsWrapper);
